import type { DataSource, LayerId } from '../types/entity';
import { layerColor } from '../styles/tokens';
import { LayerOrchestrator } from './LayerOrchestrator';
import { openPanel, isPanelOpen, closePanel, closeEntityPopupIfOpen, type PanelId } from './panelStack';

/**
 * Chips de camada horizontais roláveis — implementa o que a
 * especificação original do projeto já pedia desde o início
 * ("camadas ativas em chips horizontais roláveis no topo") mas que
 * nunca tinha sido de fato construído em nenhuma etapa anterior
 * (confirmado por grep: zero ocorrência de `.layer-chip` antes desta
 * auditoria). Visível só no mobile — a partir do tablet a estética
 * original (painéis sempre visíveis nos cantos) já cobre a necessidade
 * de "ver o que está ativo" sem precisar de chip.
 *
 * Dois tipos de chip:
 * 1. Chips de camada de mapa (flights, earthquakes, etc.) — toggle
 *    liga/desliga via LayerOrchestrator.activate()/deactivate().
 * 2. Chips de painel (news, recon) — não são camadas de mapa, são
 *    toggles que abrem/fecham o bottom sheet correspondente via
 *    panelStack (ver global.css .side-panel e panelStack.ts).
 */

interface MapLayerChipConfig {
  layer: LayerId;
  label: string;
  source: DataSource;
}

interface PanelChipConfig {
  panelId: PanelId;
  label: string;
}

const LAYER_LABELS: Partial<Record<LayerId, string>> = {
  flights: 'Voos',
  earthquakes: 'Sismos',
  'webcams-windy': 'Câmeras (Windy)',
  'webcams-wsdot': 'Câmeras (WSDOT)',
  'webcams-der-sp': 'Câmeras (DER-SP)',
  wildfires: 'Incêndios',
  satellites: 'Satélites',
  maritime: 'Marítimo',
};

function createMapLayerChip(config: MapLayerChipConfig, orchestrator: LayerOrchestrator): HTMLButtonElement {
  const chip = document.createElement('button');
  chip.className = 'layer-chip';
  chip.style.setProperty('--chip-color', layerColor[config.layer] ?? '#3FD4FF');
  chip.setAttribute('aria-pressed', 'true'); // ativo por padrão — main.ts já ativa todas as fontes no load

  const dot = document.createElement('span');
  dot.className = 'layer-chip-dot';

  const label = document.createElement('span');
  label.textContent = config.label;

  chip.append(dot, label);

  chip.addEventListener('click', () => {
    closeEntityPopupIfOpen(); // CORREÇÃO Bug 4: chip de camada de mapa tocado fecha popup de entidade aberto, em qualquer breakpoint

    const isActive = chip.getAttribute('aria-pressed') === 'true';

    if (isActive) {
      orchestrator.deactivate(config.layer);
      chip.setAttribute('aria-pressed', 'false');
    } else {
      orchestrator.activate(config.source);
      chip.setAttribute('aria-pressed', 'true');
    }
  });

  return chip;
}

const PANEL_CHIP_COLORS: Record<PanelId, string> = {
  news: layerColor.news ?? '#7C8791',
  recon: '#3FD4FF', // RECON não é uma camada de mapa (não está em LayerId/layerColor) — cor de destaque própria, consistente com accent-live
};

function createPanelChip(config: PanelChipConfig): HTMLButtonElement {
  const chip = document.createElement('button');
  chip.className = 'layer-chip';
  chip.style.setProperty('--chip-color', PANEL_CHIP_COLORS[config.panelId]);
  chip.setAttribute('aria-pressed', String(isPanelOpen(config.panelId)));

  const dot = document.createElement('span');
  dot.className = 'layer-chip-dot';

  const label = document.createElement('span');
  label.textContent = config.label;

  chip.append(dot, label);

  chip.addEventListener('click', () => {
    const isOpen = isPanelOpen(config.panelId);

    if (isOpen) {
      closePanel(config.panelId);
      chip.setAttribute('aria-pressed', 'false');
    } else {
      openPanel(config.panelId);
      chip.setAttribute('aria-pressed', 'true');

      // openPanel fecha os outros painéis registrados — sincroniza o
      // estado visual dos outros chips de painel na mesma faixa.
      for (const otherChip of chip.parentElement?.querySelectorAll('[data-panel-chip]') ?? []) {
        if (otherChip !== chip) otherChip.setAttribute('aria-pressed', 'false');
      }
    }
  });

  chip.dataset.panelChip = config.panelId;
  return chip;
}

/**
 * Monta a faixa de chips. `mapLayerSources` é a lista de todas as
 * DataSource já ativadas em main.ts (o chip reflete/controla o mesmo
 * DataSource, não duplica a lógica de fetch). `panelIds` são os
 * painéis secundários que também merecem chip próprio no mobile.
 *
 * CORREÇÃO DE BUG (sobreposição do título): recebe `headerStack` (o
 * wrapper retornado por mountHudHeader), não mais `hudRoot`
 * diretamente — a row de chips é inserida DENTRO do mesmo container
 * flex-column que o header, como segundo item da coluna, em vez de um
 * elemento `position: absolute` independente mirando a mesma
 * coordenada do header (causa raiz confirmada da sobreposição).
 */
export function mountLayerChips(
  headerStack: HTMLElement,
  orchestrator: LayerOrchestrator,
  mapLayerSources: DataSource[],
  panelChips: { panelId: PanelId; label: string }[],
): void {
  const row = document.createElement('div');
  row.className = 'layer-chips-row';

  for (const source of mapLayerSources) {
    const label = LAYER_LABELS[source.layer] ?? source.label;
    const chip = createMapLayerChip({ layer: source.layer, label, source }, orchestrator);
    row.appendChild(chip);
  }

  for (const panelChip of panelChips) {
    const chip = createPanelChip({ panelId: panelChip.panelId, label: panelChip.label });
    row.appendChild(chip);
  }

  headerStack.appendChild(row);
}
